import { useState, useEffect, useCallback } from 'react';
import './createuser.css';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import { AutoparkService, IAutopark } from '../../services/autopark.service';
import { MedicalService, IMedicLicense } from '../../services/medical.service';
import { EdsService } from '../../services/eds.service';
import PhoneInput from '../PhoneInput/PhoneInput';
import { buildCreateUserPayload, ICreateUserForm } from './createUserPayload';
import { pickFieldErrors } from '../../utils/apiError';
import { FIELD_MESSAGES } from '../../utils/fieldMessages';

// Поля POST /profiles, ошибки по которым (422, 409 login_in_use / phone_in_use) форма показывает под своим input
const CREATE_USER_FIELDS = [
  'login',
  'phoneNumber',
  'firstName',
  'middleName',
  'lastName',
  'email',
  'role',
  'status',
  'autoparkID',
  'isInternal',
] as const;

interface IMedicLicenseData {
  issueDate: string;
  number: string;
  ser: string;
}

interface IUserErrors {
  login?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  email?: string;
  phoneNumber?: string;
  role?: string;
  status?: string;
  autoparkID?: string;
  isInternal?: string;
  general?: string;
  // Ошибки для лицензии
  licenseIssueDate?: string;
  licenseNumber?: string;
  licenseSer?: string;
  // Ошибки для EDS ключей
  edsKey?: string;
}

function CreateUser() {
  const navigate = useNavigate();
  const [user, setUser] = useState<ICreateUserForm>({
    login: '',
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    phoneNumber: '',
    role: '',
    status: '',
    autoparkID: '',
    isInternal: true, // По умолчанию "Наш"
  });

  const [medicLicense, setMedicLicense] = useState<IMedicLicenseData>({
    issueDate: '',
    number: '',
    ser: '',
  });

  const [edsKeyFile, setEdsKeyFile] = useState<File | null>(null);

  const [errors, setErrors] = useState<IUserErrors>({});
  const [autoparks, setAutoparks] = useState<IAutopark[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const fetchAutoparks = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      const response = await AutoparkService.getAutoparks(page, 20);

      if (page === 1) {
        setAutoparks(response.data);
      } else {
        setAutoparks((prev) => [...prev, ...response.data]);
      }

      setCurrentPage(page);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Ошибка при загрузке автопарков:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (
      user.role === 'autopark_employee' ||
      (user.isInternal === false && (user.role === 'medic' || user.role === 'technik'))
    ) {
      fetchAutoparks(1);
    }
  }, [user.role, user.isInternal, fetchAutoparks]);

  const loadMoreAutoparks = () => {
    if (currentPage < totalPages && !loading) {
      fetchAutoparks(currentPage + 1);
    }
  };

  const validateForm = () => {
    const tempErrors: IUserErrors = {};

    if (user.email && !/\S+@\S+\.\S+/.test(user.email)) {
      tempErrors.email = FIELD_MESSAGES.invalidEmail;
    }

    if (!/^\w+$/.test(user.login)) {
      tempErrors.login = 'Логин должен содержать только буквы, цифры и нижние подчеркивания.';
    }

    if (!user.firstName) {
      tempErrors.firstName = FIELD_MESSAGES.required;
    }

    if (!user.lastName) {
      tempErrors.lastName = FIELD_MESSAGES.required;
    }

    if (!user.role) {
      tempErrors.role = 'Необходимо выбрать роль.';
    }
    // Водитель создаётся незарегистрированным, статус у него не выбирается
    if (!user.status && user.role !== 'driver') {
      tempErrors.status = 'Необходимо выбрать статус.';
    }

    if (
      (user.role === 'autopark_employee' ||
        (user.isInternal === false && (user.role === 'medic' || user.role === 'technik'))) &&
      !user.autoparkID
    ) {
      tempErrors.autoparkID = FIELD_MESSAGES.autoparkRequired;
    }

    // Валидация лицензии для медиков
    if (user.role === 'medic') {
      if (!medicLicense.issueDate) {
        tempErrors.licenseIssueDate = 'Дата выдачи лицензии обязательна.';
      }
      if (!medicLicense.number) {
        tempErrors.licenseNumber = 'Номер лицензии обязателен.';
      }
      if (!medicLicense.ser) {
        tempErrors.licenseSer = 'Серия лицензии обязательна.';
      }
    }

    // Валидация EDS ключей для медиков и техников
    if (user.role === 'medic' || user.role === 'technik') {
      if (!edsKeyFile) {
        tempErrors.edsKey = 'Необходимо загрузить архив с EDS ключами.';
      } else {
        const fileExtension = edsKeyFile.name.split('.').pop()?.toLowerCase();
        if (!fileExtension || !['zip', 'tar', 'gz', 'tar.gz'].includes(fileExtension)) {
          tempErrors.edsKey = 'Файл должен быть архивом (zip, tar, tar.gz).';
        }
      }
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors((prevState) => ({
      ...prevState,
      [name]: '',
    }));

    // Сброс данных лицензии и EDS ключей при смене роли
    if (name === 'role') {
      if (value !== 'medic') {
        setMedicLicense({
          issueDate: '',
          number: '',
          ser: '',
        });
      }
      if (value !== 'medic' && value !== 'technik') {
        setEdsKeyFile(null);
      }
    }
  };

  const handleLicenseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setMedicLicense((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors((prevState) => ({
      ...prevState,
      [`license${name.charAt(0).toUpperCase() + name.slice(1)}`]: '',
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setEdsKeyFile(file);
    setErrors((prevState) => ({
      ...prevState,
      edsKey: '',
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const booleanValue = value === 'true';
    setUser((prevState) => ({
      ...prevState,
      [name]: booleanValue,
      // Если выбираем "Наш", очищаем выбранный автопарк
      autoparkID: booleanValue ? '' : prevState.autoparkID,
    }));
    setErrors((prevState) => ({ ...prevState, [name]: '', autoparkID: '' }));
  };

  const handlePhoneChange = (numericValue: string) => {
    setUser((prevState) => ({
      ...prevState,
      phoneNumber: numericValue,
    }));

    // Формат проверяет сервер (ошибка придёт под полем); здесь только снимаем прошлую ошибку
    setErrors((prevState) => ({ ...prevState, phoneNumber: '' }));
  };

  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const resetForm = () => {
    setUser({
      login: '',
      firstName: '',
      lastName: '',
      middleName: '',
      email: '',
      phoneNumber: '',
      role: '',
      status: '',
      autoparkID: '',
      isInternal: true,
    });
    setMedicLicense({
      issueDate: '',
      number: '',
      ser: '',
    });
    setEdsKeyFile(null);
    setErrors({});
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    // validateForm требует роль; проверка role сужает тип для payload
    const { role } = user;
    if (validateForm() && role) {
      try {
        const registrationResponse = await AuthService.registration(buildCreateUserPayload({ ...user, role }));

        const userId = (registrationResponse as any).id;

        // Если регистрируем медика, отправляем лицензию
        if (user.role === 'medic' && registrationResponse && userId) {
          try {
            const licenseData: IMedicLicense = {
              issueDate: formatDateForAPI(medicLicense.issueDate),
              number: medicLicense.number,
              profileID: userId,
              ser: medicLicense.ser,
            };

            await MedicalService.createMedicLicense(licenseData);
          } catch (licenseError: any) {
            console.error('Ошибка при создании лицензии:', licenseError);
            // Не показываем ошибку пользователю, так как пользователь уже зарегистрирован
          }
        }

        // Если регистрируем медика или техника, отправляем EDS ключи
        if ((user.role === 'medic' || user.role === 'technik') && registrationResponse && userId && edsKeyFile) {
          try {
            await EdsService.uploadEdsKey(edsKeyFile, userId);
          } catch (edsError: any) {
            console.error('Ошибка при загрузке EDS ключей:', edsError);
            // Не показываем ошибку пользователю, так как пользователь уже зарегистрирован
          }
        }

        resetForm();
        setErrors({});
        navigate('/account/admin/users');
      } catch (error: any) {
        // Ошибки полей — под полями текстом сервера, остальное — общей строкой
        const fieldErrors = pickFieldErrors(error, CREATE_USER_FIELDS);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prevErrors) => ({ ...prevErrors, ...fieldErrors }));
          return;
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          general: error.message || 'Произошла ошибка при регистрации.',
        }));
      }
    }
  };

  // Проверяем, нужно ли показывать селект автопарка
  const shouldShowAutoparkSelect =
    user.role === 'autopark_employee' ||
    (user.isInternal === false && (user.role === 'medic' || user.role === 'technik'));

  return (
    <div className="create-user">
      <header className="create-user__header">Регистрация нового пользователя</header>
      <form onSubmit={handleSubmit} onError={(errors) => console.log(errors)}>
        {true && <div className="error general-error">{errors.general}</div>}
        <div className="form-group">
          <label htmlFor="login">Логин</label>
          <input
            placeholder="Обязательное поле"
            type="text"
            id="login"
            name="login"
            value={user.login}
            onChange={handleChange}
            required
          />
          {errors.login && <div className="error">{errors.login}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="phoneNumber">Номер телефона</label>
          <PhoneInput
            placeholder="Обязательное поле"
            id="phoneNumber"
            value={user.phoneNumber}
            onChangeValue={handlePhoneChange}
            required
            customInput={true}
          />
          {errors.phoneNumber && <div className="error">{errors.phoneNumber}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="lastName">Фамилия</label>
          <input
            placeholder="Обязательное поле"
            type="text"
            id="lastName"
            name="lastName"
            value={user.lastName}
            onChange={handleChange}
            required
          />
          {errors.lastName && <div className="error">{errors.lastName}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="firstName">Имя</label>
          <input
            placeholder="Обязательное поле"
            type="text"
            id="firstName"
            name="firstName"
            value={user.firstName}
            onChange={handleChange}
            required
          />
          {errors.firstName && <div className="error">{errors.firstName}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="middleName">Отчество</label>
          <input
            placeholder="Необязательное поле"
            type="text"
            id="middleName"
            name="middleName"
            value={user.middleName}
            onChange={handleChange}
          />
          {errors.middleName && <div className="error">{errors.middleName}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="role">Роль</label>
          <select className="select-style" id="role" name="role" value={user.role} onChange={handleChange} required>
            <option value="">Выберите роль</option>
            <option value="medic">Медик</option>
            <option value="driver">Водитель</option>
            <option value="technik">Техник</option>
            <option value="admin">Админ</option>
            <option value="autopark_employee">Представитель автопарка</option>
          </select>
          {errors.role && <div className="error">{errors.role}</div>}
        </div>

        {(user.role === 'medic' || user.role === 'technik') && (
          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '10px' }}>Чей сотрудник</label>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="isInternal-our"
                  name="isInternal"
                  value="true"
                  checked={user.isInternal === true}
                  onChange={handleRadioChange}
                />
                <label htmlFor="isInternal-our">Наш</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="isInternal-autopark"
                  name="isInternal"
                  value="false"
                  checked={user.isInternal === false}
                  onChange={handleRadioChange}
                />
                <label htmlFor="isInternal-autopark">Сотрудник автопарка</label>
              </div>
            </div>
            {errors.isInternal && <div className="error">{errors.isInternal}</div>}
          </div>
        )}

        {user.role === 'medic' && (
          <>
            <div className="form-group">
              <label htmlFor="licenseNumber">Номер лицензии МО</label>
              <input
                placeholder="Обязательное поле"
                type="text"
                id="licenseNumber"
                name="number"
                value={medicLicense.number}
                onChange={handleLicenseChange}
                required
              />
              {errors.licenseNumber && <div className="error">{errors.licenseNumber}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="licenseSer">Серия лицензии МО</label>
              <input
                placeholder="Обязательное поле"
                type="text"
                id="licenseSer"
                name="ser"
                value={medicLicense.ser}
                onChange={handleLicenseChange}
                required
              />
              {errors.licenseSer && <div className="error">{errors.licenseSer}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="licenseIssueDate">Дата выдачи лицензии МО</label>
              <input
                placeholder="Обязательное поле"
                type="date"
                id="licenseIssueDate"
                name="issueDate"
                value={medicLicense.issueDate}
                onChange={handleLicenseChange}
                required
              />
              {errors.licenseIssueDate && <div className="error">{errors.licenseIssueDate}</div>}
            </div>
          </>
        )}

        {(user.role === 'medic' || user.role === 'technik') && (
          <div className="form-group">
            <label htmlFor="edsKey">Архив с EDS ключами</label>
            <input
              type="file"
              id="edsKey"
              name="edsKey"
              accept=".zip,.tar,.tar.gz,.gz"
              onChange={handleFileChange}
              required
            />
            {edsKeyFile && (
              <div className="file-info" style={{ marginTop: '5px', fontSize: '14px', color: '#666' }}>
                Выбран файл: {edsKeyFile.name}
              </div>
            )}
            {errors.edsKey && <div className="error">{errors.edsKey}</div>}
          </div>
        )}

        {shouldShowAutoparkSelect && (
          <div className="form-group">
            <label htmlFor="autoparkID">Автопарк</label>
            <select
              className="selectStyle"
              id="autoparkID"
              name="autoparkID"
              value={user.autoparkID}
              onChange={handleChange}
              required
            >
              <option value="">Выберите автопарк</option>
              {loading && currentPage === 1 ? (
                <option disabled>Загрузка автопарков...</option>
              ) : (
                autoparks.map((autopark) => (
                  <option key={autopark.id} value={autopark.id}>
                    {autopark.tradeName || autopark.legalName || autopark.id}
                  </option>
                ))
              )}
            </select>
            {currentPage < totalPages && (
              <button
                type="button"
                className="btn"
                onClick={loadMoreAutoparks}
                disabled={loading}
                style={{ marginTop: '10px' }}
              >
                {loading ? 'Загрузка...' : 'Загрузить еще автопарки'}
              </button>
            )}
            {errors.autoparkID && <div className="error">{errors.autoparkID}</div>}
          </div>
        )}
        <div className="form-group">
          <label htmlFor="email">E-мейл</label>
          <input
            placeholder="Обязательное поле"
            type="email"
            id="email"
            name="email"
            required
            value={user.email}
            onChange={handleChange}
          />
          {errors.email && <div className="error">{errors.email}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="status">Статус</label>
          {user.role === 'driver' ? (
            <div id="status">Не активен: водитель станет активным, когда завершит регистрацию в приложении</div>
          ) : (
            <select
              className="selectStyle"
              id="status"
              name="status"
              value={user.status}
              onChange={handleChange}
              required
            >
              <option value="">Выберите статус</option>
              <option value="active">Активен</option>
              <option value="noactive">Не активен</option>
            </select>
          )}
          {errors.status && <div className="error">{errors.status}</div>}
        </div>

        <div className="form-actions">
          <button type="submit" className={`btn btn-primary`}>
            Зарегистрировать
          </button>
          <button className="btn" onClick={resetForm}>
            Сбросить
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateUser;
